import { isAccidental } from 'shared/definitions/musicTheory/notesAndAccidentals';
import InteractivePrototype from './interactivePrototype.ts';
import * as Notation from '../notation.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import utils from '../utils.ts';
import { toHTML } from '../uTheory.Font/font.ts';
import KeySigPlayer from './keySigPlayer.ts';

class NumberOfSharpsOrFlats extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.keySigPlayer = new KeySigPlayer();
    this.targetSpeedFromSkills = NumberOfSharpsOrFlats.registry.skills;
    this.settings = {
      instructions: {
        ariaLive: 'assertive',
        ariaLiveRegion: 'all',
      },
      editor: false,
      notation: false,
      piano: false,
      letters: {
        numberButtons: {
          from: 0,
          to: 7,
        },
        keySigButtons: true,
        checkButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.answer = {
      number: '',
      accidental: '',
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    this.skillCalculator = new SkillCalculator();

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions() {
    const options = this.options;
    this.questions = this.getQuestions(options);
    this.maxQuestions = this.questions.length;
  }

  getQuestions(options) {
    const drill = options.drill !== undefined && options.drill === true;
    return drill === true
      ? this.generateDrillQuestions(options)
      : this.generateManualQuestions(options);
  }

  generateManualQuestions(options) {
    return this.processQuestions(options.questions);
  }

  generateDrillQuestions(options) {
    const generator = new QuestionGenerator();
    const questions = generator.generate(options.skills, options.maxQuestions || 8, options);

    return this.processQuestions(questions);
  }

  processQuestions(questions) {
    return questions
      .map((question, index) => {
        const questionParts = question.split('/');
        const keyNote = questionParts[0];
        const keyQuality = questionParts[1];
        const keySig = new Notation.KeySignature(keyNote, keyQuality);
        const skills = this.calculateSkill(keySig);

        // if (question === 'C/M' || question === 'A/m') {
        //   return false;
        // }

        return {
          question: keySig,
          answer: keySig.accidentalsCount,
          text: index + 1,
          skills,
        };
      })
      .filter((q) => q);
  }

  createCustomDivs() {
    this.createAnswerDiv('div', 'answerDiv', 1);
    this.answerDiv.setAttribute('aria-live', 'polite');
  }

  createAnswerDiv(element, elementId, index) {
    this[elementId] = document.createElement(element);

    const div = this[elementId];
    const styleOptions = {
      fontSize: '4vw',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '10px',
    };

    div.id = elementId;
    div.innerHTML = '?';

    utils.merge(div.style, styleOptions);

    this.displayDiv.insertBefore(div, this.displayDiv.children[index]);
  }

  newQuestion() {
    this.clearAnswer();
    this.progressTracker.start(this.targetSpeed);
    this.questionPrompt = this.getQuestionPrompt();

    super.newQuestion();
  }

  getQuestionPrompt() {
    let questionPrompt;
    const question = this.questions[this.questionNumber].question;
    const key = `${question.keyNote.toAccidentalFontShortString()} ${question.quality}`;
    const testMode = this.optionsTrue('testMode');

    if (testMode === true) {
      questionPrompt = `How many sharps/flats in ${key}`;
    } else {
      questionPrompt = `How many sharps/flats in ${key}<br/> <small>Question {question} of ${this.questions.length}</small><br />`;
    }

    return questionPrompt;
  }

  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.clearAnswer();
      const questionParts = questionString.split('/');
      const keyNote = questionParts[0];
      const keyQuality = questionParts[1];
      const keySig = new Notation.KeySignature(keyNote, keyQuality);
      const skills = this.calculateSkill(keySig);
      const questions = [];

      questions.push({
        question: keySig,
        answer: keySig.accidentalsCount,
        skills,
        keyNote,
      });

      this.questions = questions;

      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners() {
    this.letterPanel.addNumberCallback(this.addNumberToAnswer, this);
    this.letterPanel.addKeySigCallback(this.addAccidentalToAnswer, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  addNumberToAnswer(number) {
    this.answer.number = number;
    this.displayAnswer();
  }

  addAccidentalToAnswer(accidental) {
    if (accidental === 'delete') {
      if (this.answer.accidental) {
        this.answer.accidental = '';
      } else {
        this.answer.number = '';
      }
    } else if (isAccidental(accidental)) {
      this.answer.accidental = accidental;
    }
    this.displayAnswer();
  }

  displayAnswer() {
    if (!this.answer.number && !this.answer.accidental) {
      this.answerDiv.innerHTML = '?';
      return;
    }
    const accidental = toHTML('accidental', this.answer.accidental || '');
    this.answerDiv.innerHTML = `${this.answer.number}<span class="accidentals">${accidental}</span>`;
  }

  calculateSkill(key) {
    return this.skillCalculator.getKeySignatureSkill(key);
  }

  isCorrect() {
    const rightAnswer = `${this.questions[this.questionNumber].answer}`;
    const direction =
      this.answer.accidental === 'b' ? '-' : this.answer.accidental === '#' ? '' : '';
    const userAnswer = `${direction}${this.answer.number}`;
    return rightAnswer === userAnswer;
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return;
    // If they haven't listed an accidental, require it:
    if (this.currentQuestion.answer !== 0 && this.answer.accidental === '') {
      this.checkMark.warn(`${this.answer.number} sharps or flats?`);
      return;
    }

    const correct = this.isCorrect();

    const testMode = this.optionsTrue('testMode');

    this.answerAttempts++;

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.getAnswerAndData(true);
      this.rightAnswer(800);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  clearAnswer() {
    const answer = this.answer;

    Object.keys(answer).forEach((i) => {
      if (answer.hasOwnProperty(i)) {
        answer[i] = '';
      }
    });

    this.answerDiv.innerHTML = '?';
  }

  getAnswerAndData(correct = this.isCorrect()) {
    const accCount = this.questions[this.questionNumber].answer;
    const accidental = accCount === 0 ? '' : accCount > 0 ? '#' : 'b';
    const correctAnswer = Math.abs(accCount) + accidental;

    const answerAndData = {
      question: `${this.questions[this.questionNumber].question}`,
      questionType: 'NumberOfSharpsOrFlats',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: this.answer.number + this.answer.accidental,
      studentAnswerSVG: null,
      correctAnswerText: correctAnswer,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber].skills,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    this.displayDiv.removeChild(this.answerDiv);
    super.gameOver();
  }

  playAnswer() {
    this.keySigPlayer.playAnswer(this.currentQuestion.question);
  }
}

NumberOfSharpsOrFlats.registry = {
  description: 'How many sharps/flats in a key?',
  skills: ['sharp_majors', 'flat_majors', 'sharp_minors', 'flat_minors'],
};

export default NumberOfSharpsOrFlats;
